"use client"

import { useState, useEffect } from "react"
import { Card, CardHeader, CardBody, Button, Row, Col, Badge } from "reactstrap"
import { Edit, Trash2, Users, DollarSign, Calendar, TrendingUp, AlertCircle, Package } from 'lucide-react'
import { getPlan } from "../../../../../api-handle/subscriptionApi"
import Loader from "../../../../../CommonElements/Loader/loader"
import "./SubscriptionCard.css"

const SubscriptionCard = ({ onEdit, onDeactivate, refetch }) => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")

    useEffect(() => {
        getAllPlans()
    }, [refetch])

    const getAllPlans = async () => {
        try {
            setLoading(true)
            const response = await getPlan()
            if (response && response.plans) {
                setData(response.plans)
            } else {
                setData([])
            }
        } catch (error) {
            console.error("Failed to fetch Plans:", error)
            setError("Failed to fetch plans. Please try again later.")
        } finally {
            setLoading(false)
        }
    }

    const formatDuration = (duration, durationType) => {
        const typeMap = {
            weeks: { singular: "Week", plural: "Weeks" },
            months: { singular: "Month", plural: "Months" },
            days: { singular: "Day", plural: "Days" },
            years: { singular: "Year", plural: "Years" },
        }
        const type = durationType.toLowerCase()
        const mappedType = typeMap[type] || {
            singular: type,
            plural: type.endsWith("s") ? type : type + "s"
        }
        return duration === 1 ? mappedType.singular : mappedType.plural
    }

    const getStatusColor = (status) => {
        return status === "active" ? "success" : "secondary"
    }

    const getPlanIcon = (index) => {
        const icons = [Package, TrendingUp, Users]
        const IconComponent = icons[index % icons.length]
        return <IconComponent className="plan-icon" />
    }

    if (loading) {
        return (
            <div className="subscription-loader">
                <Loader />
            </div>
        )
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-content">
                    <AlertCircle className="error-icon" />
                    <h4>Oops! Something went wrong</h4>
                    <p>{error}</p>
                    <Button color="primary" onClick={getAllPlans} className="retry-btn">
                        Try Again
                    </Button>
                </div>
            </div>
        )
    }

    if (data.length === 0) {
        return (
            <div className="empty-state">
                <div className="empty-content">
                    <Package className="empty-icon" />
                    <h4>No Subscription Plans Found</h4>
                    <p>Create your first subscription plan to get started.</p>
                </div>
            </div>
        )
    }

    return (
        <div >


            <Row className="subscription-grid">
                {data.map((plan, index) => (
                    <Col key={plan._id} lg={4} md={6} sm={12} className="mb-4">
                        <Card className="subscription-plan-card">
                            <div className="card-header-custom">
                                <div className="plan-header">
                                    <div className="plan-icon-wrapper">
                                        {getPlanIcon(index)}
                                    </div>
                                    <div className="plan-info">
                                        <h5 className="plan-name">{plan.name}</h5>
                                        <Badge
                                            color={getStatusColor(plan.status)}
                                            className="status-badge"
                                        >
                                            {plan.status.charAt(0).toUpperCase() + plan.status.slice(1).toLowerCase()}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            <CardBody className="card-body-custom">
                                <div className="plan-description">
                                    <p>{plan.description}</p>
                                </div>

                                <div className="plan-pricing">
                                    <div className="price-display">
                                        <span className="currency">KWD</span>
                                        <span className="price">{plan.price}</span>
                                        <span className="duration">
                                            / {plan.duration} {formatDuration(plan.duration, plan.durationType)}
                                        </span>
                                    </div>
                                </div>

                                <div className="plan-stats">
                                    <div className="stat-item">
                                        <div className="stat-icon-wrapper subscribers">
                                            <Users className="stat-icon" />
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-value">{plan.totalSubscribers}</span>
                                            <span className="stat-label">Subscribers</span>
                                        </div>
                                    </div>

                                    <div className="stat-item">
                                        <div className="stat-icon-wrapper revenue">
                                            <DollarSign className="stat-icon" />
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-value">KWD {plan.totalRevenue}</span>
                                            <span className="stat-label">Revenue</span>
                                        </div>
                                    </div>

                                    <div className="stat-item">
                                        <div className="stat-icon-wrapper duration">
                                            <Calendar className="stat-icon" />
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-value">{plan.duration}</span>
                                            <span className="stat-label">{formatDuration(plan.duration, plan.durationType)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="action-buttons">
                                    <Button
                                        color="primary"
                                        className="edit-btn"
                                        onClick={() => onEdit(plan._id)}
                                    >
                                        <Edit className="btn-icon" />
                                        Edit Plan
                                    </Button>

                                    <Button
                                        color={plan.status === "inactive" ? "secondary" : "danger"}
                                        className={`${plan.status === "inactive" ? "reactivate-btn" : "deactivate-btn"}`}
                                        onClick={() => onDeactivate(plan._id, plan?.status)}
                                    // disabled={plan.status === "inactive"}
                                    >
                                        {plan.status == "active" && (
                                            <Trash2 className="btn-icon" />
                                        )}
                                        {plan.status === "inactive" ? "Reactivate" : "Deactivate"}
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    )
}

export default SubscriptionCard
